import React from 'react';
import { useTranslation } from 'react-i18next';

import type { McpServer, McpServerWritePayload } from '../services/mcpService';
import styles from './McpToolsPage.module.css';

export type ServerFormState = {
  displayName: string;
  description: string;
  endpointUrl: string;
  transport: string;
  authType: string;
  authHeaderName: string;
  authValue: string;
  oauthGrantType: string;
  oauthAuthorizationUrl: string;
  oauthTokenUrl: string;
  oauthScopes: string;
  oauthClientId: string;
  oauthClientSecret: string;
  clearOAuthClientSecret: boolean;
  clearOAuthTokens: boolean;
};

export const emptyServerForm: ServerFormState = {
  displayName: '',
  description: '',
  endpointUrl: '',
  transport: 'streamableHttp',
  authType: 'none',
  authHeaderName: 'X-Api-Key',
  authValue: '',
  oauthGrantType: 'authorizationCode',
  oauthAuthorizationUrl: '',
  oauthTokenUrl: '',
  oauthScopes: '',
  oauthClientId: '',
  oauthClientSecret: '',
  clearOAuthClientSecret: false,
  clearOAuthTokens: false,
};

export const loadServerForm = (server: McpServer | null): ServerFormState => {
  if (!server) {
    return emptyServerForm;
  }

  return {
    displayName: server.displayName,
    description: server.description,
    endpointUrl: server.endpointUrl,
    transport: server.transport || 'streamableHttp',
    authType: server.authType,
    authHeaderName: server.authHeaderName,
    authValue: '',
    oauthGrantType: server.oauthGrantType || 'authorizationCode',
    oauthAuthorizationUrl: server.oauthAuthorizationUrl || '',
    oauthTokenUrl: server.oauthTokenUrl || '',
    oauthScopes: server.oauthScopes || '',
    oauthClientId: server.oauthClientId || '',
    oauthClientSecret: '',
    clearOAuthClientSecret: false,
    clearOAuthTokens: false,
  };
};

export const toServerPayload = (form: ServerFormState): McpServerWritePayload => ({
  displayName: form.displayName.trim(),
  description: form.description.trim() || undefined,
  endpointUrl: form.endpointUrl.trim(),
  transport: form.transport,
  authType: form.authType,
  authHeaderName: form.authHeaderName.trim() || undefined,
  authValue: form.authValue.trim() || undefined,
  oauthGrantType: form.oauthGrantType,
  oauthAuthorizationUrl: form.oauthAuthorizationUrl,
  oauthTokenUrl: form.oauthTokenUrl,
  oauthScopes: form.oauthScopes,
  oauthClientId: form.oauthClientId,
  oauthClientSecret: form.oauthClientSecret.trim() || undefined,
  clearOAuthClientSecret: form.clearOAuthClientSecret,
  clearOAuthTokens: form.clearOAuthTokens,
});

type McpServerFormProps = {
  form: ServerFormState;
  /** Null while creating, which also means there are no stored credentials to hint at. */
  server: McpServer | null;
  mode: 'create' | 'edit';
  onChange: React.Dispatch<React.SetStateAction<ServerFormState>>;
};

export const McpServerForm: React.FC<McpServerFormProps> = ({
  form,
  server,
  mode,
  onChange,
}) => {
  const { t } = useTranslation('mcp');

  // Catalog connectors usually discover a client during connect. Show the
  // fields when this catalog server still needs a tenant-supplied OAuth app.
  const showManualOAuthFields = form.authType === 'oauth'
    && !(mode === 'edit' && Boolean(server?.catalogProviderId) && Boolean(server?.oauthClientId));

  const transportOptions = [
    {
      value: 'streamableHttp',
      title: t('transportStreamableHttp'),
      hint: t('transportStreamableHttpHint'),
    },
    {
      value: 'sseLegacy',
      title: t('transportSseLegacy'),
      hint: t('transportSseLegacyHint'),
    },
  ];

  return (
    <>
      <div className={styles.formGrid}>
        <label className={`${styles.field} ${styles.fieldWide}`}>
          <span className={styles.fieldLabel}>{t('displayName')}</span>
          <input
            className="text-input"
            value={form.displayName}
            placeholder={t('displayNamePlaceholder')}
            onChange={(event) => onChange((current) => ({
              ...current,
              displayName: event.target.value,
            }))}
          />
        </label>

        <label className={`${styles.field} ${styles.fieldWide}`}>
          <span className={styles.fieldLabel}>{t('description')}</span>
          <input
            className="text-input"
            value={form.description}
            placeholder={t('descriptionPlaceholder')}
            onChange={(event) => onChange((current) => ({
              ...current,
              description: event.target.value,
            }))}
          />
        </label>

        <label className={`${styles.field} ${styles.fieldWide}`}>
          <span className={styles.fieldLabel}>{t('endpointUrl')}</span>
          <input
            className="text-input"
            value={form.endpointUrl}
            placeholder="https://example.com/mcp"
            onChange={(event) => onChange((current) => ({
              ...current,
              endpointUrl: event.target.value,
            }))}
          />
        </label>

        <div className={`${styles.field} ${styles.fieldWide}`}>
          <span className={styles.fieldLabel}>{t('transport')}</span>
          <div className={styles.optionCards}>
            {transportOptions.map((option) => (
              <label
                key={option.value}
                className={`${styles.optionCard} ${form.transport === option.value ? styles.optionCardSelected : ''}`}
              >
                <input
                  type="radio"
                  name="mcp-transport"
                  value={option.value}
                  checked={form.transport === option.value}
                  onChange={() => onChange((current) => ({
                    ...current,
                    transport: option.value,
                  }))}
                />
                <span>
                  <span className={styles.optionCardTitle}>{option.title}</span>
                  <span className={styles.optionCardHint}>{option.hint}</span>
                </span>
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className={styles.formGrid}>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('authType')}</span>
          <select
            className="text-input"
            value={form.authType}
            onChange={(event) => onChange((current) => ({
              ...current,
              authType: event.target.value,
            }))}
          >
            <option value="none">{t('authNone')}</option>
            <option value="bearer">{t('authBearer')}</option>
            <option value="apiKey">{t('authApiKey')}</option>
            <option value="basic">{t('authBasic')}</option>
            <option value="oauth">{t('authOAuth')}</option>
          </select>
        </label>

        {form.authType === 'apiKey' ? (
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('authHeaderName')}</span>
            <input
              className="text-input"
              value={form.authHeaderName}
              onChange={(event) => onChange((current) => ({
                ...current,
                authHeaderName: event.target.value,
              }))}
            />
          </label>
        ) : null}

        {form.authType !== 'none' && form.authType !== 'oauth' ? (
          <label className={`${styles.field} ${styles.fieldWide}`}>
            <span className={styles.fieldLabel}>{t('authValue')}</span>
            <input
              className="text-input"
              type="password"
              value={form.authValue}
              placeholder={server?.authConfigured
                ? t('authValueKeepPlaceholder')
                : t('authValuePlaceholder')}
              onChange={(event) => onChange((current) => ({
                ...current,
                authValue: event.target.value,
              }))}
            />
          </label>
        ) : null}

        {showManualOAuthFields ? (
          <>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('oauthGrantType')}</span>
              <select
                className="text-input"
                value={form.oauthGrantType}
                onChange={(event) => onChange((current) => ({
                  ...current,
                  oauthGrantType: event.target.value,
                }))}
              >
                <option value="authorizationCode">{t('oauthGrantAuthorizationCode')}</option>
                <option value="clientCredentials">{t('oauthGrantClientCredentials')}</option>
              </select>
            </label>

            {form.oauthGrantType === 'authorizationCode' ? (
              <label className={`${styles.field} ${styles.fieldWide}`}>
                <span className={styles.fieldLabel}>{t('oauthAuthorizationUrl')}</span>
                <input
                  className="text-input"
                  value={form.oauthAuthorizationUrl}
                  placeholder="https://provider.example.com/oauth/authorize"
                  onChange={(event) => onChange((current) => ({
                    ...current,
                    oauthAuthorizationUrl: event.target.value,
                  }))}
                />
              </label>
            ) : null}

            <label className={`${styles.field} ${styles.fieldWide}`}>
              <span className={styles.fieldLabel}>{t('oauthTokenUrl')}</span>
              <input
                className="text-input"
                value={form.oauthTokenUrl}
                placeholder="https://provider.example.com/oauth/token"
                onChange={(event) => onChange((current) => ({
                  ...current,
                  oauthTokenUrl: event.target.value,
                }))}
              />
            </label>

            <label className={`${styles.field} ${styles.fieldWide}`}>
              <span className={styles.fieldLabel}>{t('oauthScopes')}</span>
              <input
                className="text-input"
                value={form.oauthScopes}
                placeholder="read write"
                onChange={(event) => onChange((current) => ({
                  ...current,
                  oauthScopes: event.target.value,
                }))}
              />
            </label>

            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('oauthClientId')}</span>
              <input
                className="text-input"
                value={form.oauthClientId}
                onChange={(event) => onChange((current) => ({
                  ...current,
                  oauthClientId: event.target.value,
                }))}
              />
            </label>

            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('oauthClientSecret')}</span>
              <input
                className="text-input"
                type="password"
                value={form.oauthClientSecret}
                placeholder={server?.oauthClientSecretConfigured
                  ? t('oauthClientSecretPlaceholderConfigured')
                  : t('oauthClientSecretPlaceholder')}
                onChange={(event) => onChange((current) => ({
                  ...current,
                  oauthClientSecret: event.target.value,
                }))}
              />
            </label>

            {server?.oauthClientSecretConfigured ? (
              <label className={`${styles.checkRow} ${styles.fieldWide}`}>
                <input
                  type="checkbox"
                  checked={form.clearOAuthClientSecret}
                  onChange={(event) => onChange((current) => ({
                    ...current,
                    clearOAuthClientSecret: event.target.checked,
                    oauthClientSecret: event.target.checked ? '' : current.oauthClientSecret,
                  }))}
                />
                <span className={styles.checkRowLabel}>{t('clearOAuthClientSecret')}</span>
              </label>
            ) : null}
          </>
        ) : null}
      </div>
    </>
  );
};
